import { computed, watch } from 'vue';
import type { RouteLocationNormalized } from 'vue-router';
import { normalizeNamespaceAndPath } from '~/helpers';
import { entitlementForAny, type Verdict } from '~/plugins/entitlement';
import { featuresStateLoaded } from '~/plugins/feature';
import { usePaywallStore } from '~/stores/paywall';

export interface GateRedirect {
    name: string;
    params: { catchAll: string[] };
    query: RouteLocationNormalized['query'] & Record<string, unknown>;
    hash: string;
}

/**
 * Normalizes bare gate keys (e.g. `mod-manager`) against the TARGET route's
 * namespace. The plugins' own normalization falls back to the current route,
 * which is still the previous location while middlewares run.
 */
export function normalizeGateValue(value: string | string[], to: RouteLocationNormalized): string | string[] {
    const routeName = to.name?.toString();

    return Array.isArray(value) ? value.map((entry) => normalizeNamespaceAndPath(entry, routeName)) : normalizeNamespaceAndPath(value, routeName);
}

const notFound = (to: RouteLocationNormalized): GateRedirect => ({
    name: '404',
    params: {
        catchAll: location.pathname.substring(1).split('/'),
    },
    query: to.query,
    hash: to.hash,
});

/**
 * Sends an admin to the paywall instead of a 404 when a plan or addon would unlock the
 * route. The verdict is stashed for the page to render, and the feature key is echoed
 * into the query so a refresh or shared link can rebuild it -- only the key travels, and
 * the verdict is re-derived, so a non-admin opening the same URL still gets `hidden`.
 */
export function paymentRequired(to: RouteLocationNormalized, verdict: Verdict & { state: 'locked' }): GateRedirect {
    usePaywallStore().openFor(verdict, to.name?.toString());

    const from = to.name?.toString();

    return {
        name: '402',
        params: { catchAll: location.pathname.substring(1).split('/') },
        query: { ...to.query, feature: verdict.feature, ...(from ? { from } : {}) },
        hash: to.hash,
    };
}

/**
 * Entitlement-aware gate. Unlike `resolveGate`'s boolean, a feature can fail in two
 * commercially different ways -- see `~/plugins/entitlement`.
 */
export function resolveEntitlementGate(to: RouteLocationNormalized, feature: string | string[]): Promise<GateRedirect | undefined> {
    const decide = (): GateRedirect | undefined => {
        const verdict = entitlementForAny(feature);

        if (verdict.state === 'allowed') return undefined;
        if (verdict.state === 'locked') return paymentRequired(to, verdict);

        return notFound(to);
    };

    const loaded = computed(() => featuresStateLoaded(feature));
    if (loaded.value) return Promise.resolve(decide());

    return new Promise((resolve) => {
        const unregister = watch(loaded, (isLoaded) => {
            if (!isLoaded) return;

            unregister();
            resolve(decide());
        });
    });
}

/**
 * Decides a reactive access gate, waiting for its backing state to load first
 * so cold deep-links get decided instead of silently skipped. Resolves
 * undefined when the gate passes, or a 404 redirect when it fails.
 *
 * Both callbacks must only read reactive (pinia/router) state — the wait
 * re-checks whenever that state changes.
 */
export function resolveGate(to: RouteLocationNormalized, stateLoaded: () => boolean, passes: () => boolean): Promise<GateRedirect | undefined> {
    const verdict = (): GateRedirect | undefined => (passes() ? undefined : notFound(to));

    const loaded = computed(stateLoaded);
    if (loaded.value) return Promise.resolve(verdict());

    return new Promise((resolve) => {
        const unregister = watch(loaded, (isLoaded) => {
            if (!isLoaded) return;

            unregister();
            resolve(verdict());
        });
    });
}
